import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { AbstractControl, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { RoleData } from '@telespot/authentication/ui';
import { WorkspaceItem } from '@telespot/domain';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { WorkspaceSelectorComponent } from '../../workspace/widgets/workspace-selector/workspace-selector.component';
import { CommonModule } from '@angular/common';
import { CoreModule } from '@shared/ui';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';

export interface InviteEditorData {
  enableCustomCode: boolean;
  workspaces$: Observable<WorkspaceItem[]>;
  roles$: Observable<RoleData[]>;
  hasMoreItems$: Observable<boolean>;
  workspaceSearchTerm$?: Observable<string>;
  totalWorkspaces$?: Observable<number>;
}

@Component({
  selector: 'ts-invite-editor',
  templateUrl: './invite-editor.component.html',
  styleUrls: ['./invite-editor.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatInputModule,
    MatButtonModule,
    TranslateModule,
    WorkspaceSelectorComponent,
    CommonModule,
    CoreModule,
  ],
})
export class InviteEditorComponent implements OnInit {
  public selected: WorkspaceItem[] = [];
  public initialWorkspaceSearchTerm: string | undefined;

  @Output() lastItemReached = new EventEmitter<void>();
  @Output() workspaceSearchTermChanged = new EventEmitter<string>();
  /** The user asked to select every workspace, including unloaded pages. */
  @Output() selectAllWorkspacesRequested = new EventEmitter<void>();

  public readonly form = new UntypedFormGroup({
    uses: new UntypedFormControl(1),
    code: new UntypedFormControl(undefined, [Validators.minLength(6), Validators.maxLength(10)]),
    initialRoles: new UntypedFormControl([]),
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: InviteEditorData,
    private readonly dialogRef: MatDialogRef<InviteEditorComponent>
  ) {}

  public ngOnInit() {
    // Capture the initial search term value if provided
    if (this.data.workspaceSearchTerm$) {
      this.data.workspaceSearchTerm$.pipe(take(1)).subscribe((searchTerm) => {
        this.initialWorkspaceSearchTerm = searchTerm;
      });
    }
  }

  public toggleRole(formControl: AbstractControl, role: string) {
    const { value } = formControl;
    const includesRole = value.includes(role);
    const formValue = includesRole ? value.filter((v: string) => v !== role) : [...value, role];
    formControl.setValue(formValue);
  }

  public markWorkspace(workspaces: WorkspaceItem[]) {
    this.selected = [...this.selected, ...workspaces];
  }

  public unmarkWorkspace(workspaces: WorkspaceItem[]) {
    this.selected = this.selected.filter((ws) => !workspaces.some((w) => w.id === ws.id));
  }

  public submit() {
    this.dialogRef.close({
      ...this.form.value,
      initialWorkspaces: this.selected,
    });
  }

  public cancel() {
    this.dialogRef.close();
  }
}
